import { Endpoints } from "~constants/endpoints"
import { Api } from "~utils/api"
import { asApiError } from "~utils/apiError"
import { PROOF_OF_OWNERSHIP_STATUS } from "~constants/proofOfOwnership"
import {
  AISuggestionResponse,
  AssignedQueueFilter,
  ClaimAssignmentPayload,
  PooCorrectionsPayload,
  PostPooPayload,
  ProofOfOwnership,
} from "~shapes/proofOfOwnership"

const ProofOfOwnershipService = {
  getProofsOfOwnership: async (
    status?: PROOF_OF_OWNERSHIP_STATUS,
    userId?: string,
    limit = 25,
    cursor?: string,
    sort?: string,
  ): Promise<{ data: ProofOfOwnership[]; next?: string }> => {
    let url = `${Endpoints.PROOFS_OF_OWNERSHIP_V3.URL}?limit=${limit}`
    if (sort) url = `${url}&sort=${sort}`
    if (status) url = `${url}&state=${status}`
    if (userId) url = `${url}&user_id=${userId}`
    if (cursor) url = `${url}&next=${encodeURIComponent(cursor)}`

    try {
      const response = await Api.get(url, {
        headers: { "Cache-Control": "no-cache" },
      })
      const raw = response.data
      const list =
        raw?.data ??
        raw?._embedded?.proofsOfOwnership ??
        (Array.isArray(raw) ? raw : [])
      return {
        data: Array.isArray(list) ? list : [],
        next: raw?.next || undefined,
      }
    } catch (error) {
      return Promise.reject(error)
    }
  },
  // Returns a state-keyed count map, e.g. { created: 0, validating: 1 }.
  // Keys are lowercase, matching the PROOF_OF_OWNERSHIP_STATUS enum values.
  getProofsOfOwnershipCounts: async (
    userId?: string,
  ): Promise<Record<string, number>> => {
    const query = userId ? `?user_id=${userId}` : ""
    const url = `${Endpoints.PROOFS_OF_OWNERSHIP_COUNTS.URL}${query}`

    try {
      const response = await Api.get(url, {
        headers: { "Cache-Control": "no-cache" },
      })
      return response.data ?? {}
    } catch (error) {
      return Promise.reject(error)
    }
  },
  // Single-state count via the /counts?state= path (one indexed COUNT query).
  // Used by the validation-history tab badges. Returns 0 on failure so a count
  // hiccup never blanks the page.
  getProofOfOwnershipStateCount: async (
    state: PROOF_OF_OWNERSHIP_STATUS,
  ): Promise<number> => {
    const url = `${Endpoints.PROOFS_OF_OWNERSHIP_COUNTS.URL}?state=${state}`
    try {
      const response = await Api.get(url, {
        headers: { "Cache-Control": "no-cache" },
      })
      return response.data?.count ?? 0
    } catch {
      return 0
    }
  },
  // Operator validation queue. With no filter, returns the caller's own claimed
  // (VALIDATING) POOs. Elevated callers (ROLE_ADMIN / ROLE_CUSTOMER_SUPPORT) can
  // inspect another USER's queue (assigneeId) or a SYSTEM validator's queue.
  // Same cursor pagination shape as getProofsOfOwnership.
  getAssignedProofsOfOwnership: async (
    filter: AssignedQueueFilter = {},
    limit = 25,
    cursor?: string,
    sort?: string,
  ): Promise<{ data: ProofOfOwnership[]; next?: string }> => {
    let url = `${Endpoints.PROOFS_OF_OWNERSHIP_ASSIGNED.URL}?limit=${limit}`
    if (sort) url = `${url}&sort=${sort}`
    if (filter.assigneeType) url = `${url}&assignee_type=${filter.assigneeType}`
    if (filter.assigneeId)
      url = `${url}&assignee_id=${encodeURIComponent(filter.assigneeId)}`
    if (cursor) url = `${url}&next=${encodeURIComponent(cursor)}`

    try {
      const response = await Api.get(url, {
        headers: { "Cache-Control": "no-cache" },
      })
      // The error interceptor resolves non-401/403/500 failures with the raw
      // axios error, so guard before reading data.
      if (asApiError(response)) return Promise.reject(response)
      const raw = response.data
      const list = raw?.data ?? (Array.isArray(raw) ? raw : [])
      return {
        data: Array.isArray(list) ? list : [],
        next: raw?.next || undefined,
      }
    } catch (error) {
      return Promise.reject(error)
    }
  },
  // Claim a POO for validation. With no payload the caller self-assigns and the
  // POO moves CREATED -> VALIDATING atomically. Pass a SYSTEM payload (admin only)
  // to assign an automatic validator. On 409 the POO is already being validated
  // by someone else (only a ROLE_ADMIN can override an existing claim).
  claimProofOfOwnership: async (
    documentId: string,
    options?: { payload?: ClaimAssignmentPayload; override?: boolean },
  ): Promise<ProofOfOwnership> => {
    let url = Endpoints.PROOF_OF_OWNERSHIP_ASSIGNMENT.URL.replace(
      ":id",
      documentId,
    )
    // Override (reassign an already-claimed POO) is opt-in and admin-only on the
    // server — only the explicit "Take over" action requests it. A plain claim
    // (Start validation) omits it so the server's CREATED+unassigned condition
    // still guards the race and returns 409 if someone else already took it.
    if (options?.override) url = `${url}?override=true`
    try {
      // Self-claim sends no body so the server takes its ContentLength==0
      // self-assign path; a SYSTEM payload is only sent when provided.
      const response = await Api.post(url, options?.payload)
      // A 409 ALREADY_BEING_VALIDATED (and other 4xx) resolves here as the raw
      // axios error; reject so callers can map the code instead of reading
      // `data` off an error object.
      if (asApiError(response)) return Promise.reject(response)
      return response.data
    } catch (error) {
      console.error("Error claiming proof of ownership:", error)
      return Promise.reject(error)
    }
  },
  // Release a claim: moves the POO VALIDATING -> CREATED and clears the assignee.
  // Only the claim owner (or a ROLE_ADMIN) may release. Emits no notification.
  releaseProofOfOwnership: async (
    documentId: string,
  ): Promise<ProofOfOwnership> => {
    const url = Endpoints.PROOF_OF_OWNERSHIP_ASSIGNMENT.URL.replace(
      ":id",
      documentId,
    )
    try {
      const response = await Api.delete(url)
      // A 409 NOT_ASSIGNEE (and other 4xx) resolves here as the raw axios error.
      if (asApiError(response)) return Promise.reject(response)
      return response.data
    } catch (error) {
      console.error("Error releasing proof of ownership:", error)
      return Promise.reject(error)
    }
  },
  getInvalidationReasons: async (): Promise<string[]> => {
    const url = `${Endpoints.PROOFS_OF_OWNERSHIP_INVALIDATION_REASONS.URL}`
    try {
      const response = await Api.get(url)
      return response.data
    } catch (error) {
      return Promise.reject(error)
    }
  },
  getProofsOfOwnershipFile: async (
    fileId: string,
    pooId?: string,
  ): Promise<{ url: string }> => {
    if (!fileId) return Promise.reject(new Error("No POO file id available"))

    const v3Url = Endpoints.PROOF_OF_OWNERSHIP_FILE.URL.replace(
      ":fileId",
      fileId,
    )

    try {
      const response = await Api.get(v3Url)
      return response.data
    } catch {
      const fallbackId = pooId || fileId
      const v1Url = Endpoints.PROOF_OF_OWNERSHIP.URL.replace(":id", fallbackId)
      const response = await Api.get(v1Url)
      return response.data
    }
  },
  postProofOfOwnership: async (payload: PostPooPayload): Promise<any> => {
    const payloadWithUserId = {
      broker: payload.broker,
      user_id: payload.userId,
      name: payload.name,
      source_content_type: payload.sourceContentType,
    }

    try {
      const response = await Api.post(
        Endpoints.PROOFS_OF_OWNERSHIP_V3.URL,
        payloadWithUserId,
      )
      return response.data
    } catch (error) {
      console.error("Error posting proof of ownership:", error)
      return Promise.reject(error)
    }
  },
  patchProofOfOwnership: async (
    state,
    documentId,
    invalidation_reason?: REJECTION_REASONS,
    name?: string,
    broker?: string,
    date?: string,
    person_names?: string[],
  ): Promise<any> => {
    try {
      const payload: Record<string, any> = { state }

      if (invalidation_reason !== undefined) {
        payload.invalidation_reason = invalidation_reason
      }
      if (name !== undefined) {
        payload.name = name
      }
      if (broker !== undefined) {
        payload.broker = broker
      }
      if (date !== undefined) {
        payload.date = date
      }
      if (person_names !== undefined) {
        payload.person_names = person_names
      }

      const url = `${Endpoints.PROOFS_OF_OWNERSHIP_V3.URL}/${documentId}`

      const response = await Api.patch(url, payload)
      // A non-assignee finalizing a claimed POO comes back as 409
      // INVALID_STATE_CHANGE, which the interceptor resolves as the raw axios
      // error; reject so the verdict flow doesn't treat it as a success.
      if (asApiError(response)) return Promise.reject(response)
      return response.data
    } catch (error) {
      console.error("Error patching proof of ownership:", error)
      return Promise.reject(error)
    }
  },
  patchProofOfOwnershipName: async (
    documentId: string,
    name: string,
  ): Promise<any> => {
    try {
      const url = `${Endpoints.PROOFS_OF_OWNERSHIP_V3.URL}/${documentId}`
      const response = await Api.patch(url, { name })
      return response.data
    } catch (error) {
      console.error("Error patching proof of ownership name:", error)
      return Promise.reject(error)
    }
  },
  uploadFile: async (file: File, presignedPostData: string): Promise<any> => {
    try {
      // Use fetch directly instead of Api.put to avoid automatic Authorization header
      const response = await fetch(presignedPostData, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": "application/pdf",
          "x-amz-acl": "private",
        },
      })

      if (!response.ok) {
        throw new Error(
          `Upload failed: ${response.status} ${response.statusText}`,
        )
      }

      return { success: true }
    } catch (error) {
      console.error("Upload error:", error)
      return Promise.reject(error)
    }
  },
  getProofOfOwnershipData: async (
    fileId: string,
  ): Promise<AISuggestionResponse> => {
    const url = Endpoints.PROOF_OF_OWNERSHIP_DATA.URL.replace(":fileId", fileId)
    try {
      const response = await Api.get(url)
      return response.data
    } catch (error: any) {
      // If it's a 404, it means no AI data is available for this file
      if (error?.response?.status === 404) {
        throw new Error("NO_AI_DATA")
      }
      throw error
    }
  },
  // Records the operator's final values for a reviewed POO. The server compares
  // them against the AI suggestion and computes `accepted` + per-field `edits`.
  postProofOfOwnershipDataCorrections: async (
    fileId: string,
    payload: PooCorrectionsPayload,
  ): Promise<any> => {
    try {
      const url = Endpoints.PROOF_OF_OWNERSHIP_DATA_CORRECTIONS.URL.replace(
        ":id",
        fileId,
      )
      const response = await Api.post(url, payload)
      return response.data
    } catch (error) {
      console.error("Error posting proof of ownership corrections:", error)
      return Promise.reject(error)
    }
  },
}

export default ProofOfOwnershipService
